import {
  campaignHasFields,
  getCampaign,
  type CampaignRecord,
} from 'lib/analytics/campaign';
import { getConsentGate, type ConsentGateState } from 'lib/consent/gate';
import { classifyRoute } from 'lib/consent/policy';

export const ALIAS_EVENTS = new Set([
  'Bottom bar addto cart - CLICK',
  'Rare Bottom bar addto cart - CLICK',
  'ring-air-checkout',
  'ring-pro-checkout',
  'rare-checkout',
  'ringBuyCtaClick',
  'ring_buy_cta_click',
  'ring_buy_add_to_cart',
  'ring_buy_view_item',
  'CLICK: Product Selection, Add to Cart',
]);

const VOLUME_WINDOW_MS = 1000;

type PixelNames = {
  meta?: string;
  tiktok?: string;
  reddit?: string;
};

const PIXEL_EVENTS: Record<string, PixelNames> = {
  view_item: {
    meta: 'ViewContent',
    tiktok: 'ViewContent',
    reddit: 'ViewContent',
  },
  add_to_cart: {
    meta: 'AddToCart',
    tiktok: 'AddToCart',
    reddit: 'AddToCart',
  },
  begin_checkout: {
    meta: 'InitiateCheckout',
    tiktok: 'InitiateCheckout',
  },
};

export type EcommercePayload = {
  currency: string;
  value: number;
  items: Array<{
    item_id: string;
    item_name?: string;
    price?: number;
    quantity?: number;
  }>;
};

export type EmitInput = {
  name: string;
  props?: Record<string, unknown>;
  ecommerce?: EcommercePayload;
  /** GTM name when it differs from the Vercel snake_case name. */
  gtmName?: string;
  pathname?: string;
  now?: number;
};

export type PlannedSinks = {
  vercel: boolean;
  gtm: boolean;
  pixels: string[];
  googleAds: boolean;
};

type VercelSink = (event: string, props?: Record<string, unknown>) => void;

let vercelSink: VercelSink | null = null;
const recent = new Map<string, number>();

export function registerVercelSink(sink: VercelSink): void {
  vercelSink = sink;
}

export function isAliasEvent(name: string): boolean {
  return ALIAS_EVENTS.has(name);
}

export function isDuplicateEvent(
  name: string,
  placement: string | undefined,
  now = Date.now(),
): boolean {
  const key = `${name}|${placement ?? ''}`;
  const previous = recent.get(key);
  if (previous != null && now - previous < VOLUME_WINDOW_MS) return true;
  recent.set(key, now);
  return false;
}

export function planSinks(name: string, gate: ConsentGateState): PlannedSinks {
  const pixels: string[] = [];
  const mapped = PIXEL_EVENTS[name];
  const ads = gate.stack === 'next' && gate.ads;
  if (ads && mapped?.meta) pixels.push(mapped.meta);
  if (ads && mapped?.tiktok) pixels.push(mapped.tiktok);
  if (ads && mapped?.reddit) pixels.push(mapped.reddit);
  return {
    vercel: gate.stack === 'legacy' || gate.stats,
    gtm: gate.stack === 'legacy' || gate.gtm,
    pixels,
    googleAds: Boolean(ads && mapped),
  };
}

function placementOf(props: Record<string, unknown> | undefined): string | undefined {
  const placement = props?.placement ?? props?.source;
  return typeof placement === 'string' ? placement : undefined;
}

function eventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function scriptReady(id: string): boolean {
  return typeof document !== 'undefined' && Boolean(document.getElementById(id));
}

type PixelWindow = Window & {
  dataLayer?: Record<string, unknown>[];
  fbq?: (...args: unknown[]) => void;
  ttq?: { track?: (event: string, props?: Record<string, unknown>) => void };
  rdt?: (command: string, event: string, props?: Record<string, unknown>) => void;
  gtag?: (...args: unknown[]) => void;
};

function pixelProps(
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  id: string,
): Record<string, unknown> {
  const contentIds = ecommerce?.items.map((item) => item.item_id).filter(Boolean);
  return {
    value: ecommerce?.value,
    currency: ecommerce?.currency,
    content_ids: contentIds,
    content_type: contentIds?.length ? 'product' : undefined,
    event_id: id,
    ...(campaign ?? {}),
  };
}

function sendPixels(
  name: string,
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  id: string,
): void {
  if (typeof window === 'undefined') return;
  const mapped = PIXEL_EVENTS[name];
  if (!mapped) return;
  const props = pixelProps(ecommerce, campaign, id);
  const w = window as PixelWindow;
  if (mapped.meta && scriptReady('uh-meta') && typeof w.fbq === 'function') {
    w.fbq('track', mapped.meta, props, { eventID: id });
  }
  if (mapped.tiktok && scriptReady('uh-tiktok') && w.ttq?.track) {
    w.ttq.track(mapped.tiktok, props);
  }
  if (mapped.reddit && scriptReady('uh-reddit') && typeof w.rdt === 'function') {
    w.rdt('track', mapped.reddit, props);
  }
  if (scriptReady('uh-google-ads') && typeof w.gtag === 'function') {
    w.gtag('event', name, {
      ...props,
      items: ecommerce?.items,
    });
  }
}

function sendDataLayer(
  name: string,
  gtmName: string | undefined,
  props: Record<string, unknown>,
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  id: string,
): void {
  if (typeof window === 'undefined') return;
  const w = window as PixelWindow;
  w.dataLayer ??= [];
  const eventName = gtmName ?? name;
  if (ecommerce) {
    w.dataLayer.push({ ecommerce: null });
    w.dataLayer.push({
      event: eventName,
      ecommerce,
      ...props,
      ...(campaign ?? {}),
      event_id: id,
    });
    return;
  }
  w.dataLayer.push({
    event: eventName,
    ...props,
    ...(campaign ?? {}),
    event_id: id,
  });
}

/**
 * Next-stack emission. Alias names and 1s repeats are dropped before any sink.
 * Callers on the legacy stack must not use this.
 */
export function emit(input: EmitInput): void {
  const gate = getConsentGate();
  if (gate.stack !== 'next') return;
  if (isAliasEvent(input.name) || (input.gtmName && isAliasEvent(input.gtmName))) {
    return;
  }
  const props = input.props ?? {};
  const pathname =
    input.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  if (isDuplicateEvent(input.name, placementOf(props), input.now)) return;

  const campaign =
    classifyRoute(pathname) === 'private' ? null : getCampaign(pathname);
  const attached = campaignHasFields(campaign) ? campaign : null;
  const id = eventId();
  const planned = planSinks(input.name, gate);

  if (planned.vercel && vercelSink) {
    vercelSink(input.name, { ...props, ...(attached ?? {}), event_id: id });
  }
  if (planned.gtm) {
    sendDataLayer(input.name, input.gtmName, props, input.ecommerce, attached, id);
  }
  if (planned.googleAds || planned.pixels.length > 0) {
    sendPixels(input.name, input.ecommerce, attached, id);
  }
}

export function resetFanoutForTests(): void {
  recent.clear();
}
